import _ from 'lodash';
import { Timeline, permissions, Comment, Debug, FileGallery, creativeDefaults, adapi } from '~';
import { mapActions, mapState } from 'vuex';
import CampaignNotification from '@/components/rabbit/campaign-notification/CampaignNotification.vue';
import CreativePreviewWrapper from '@/components/rabbit/creative-preview-wrapper/CreativePreviewWrapper.vue';
import { CREATIVE_STATUSES, COMMENTABLE_TYPES, CAMPAIGN_TYPES, NOTIFICATION_EVENTS } from '@/util/constants';
import CreativePreviewFeedback from '@/components/creative-preview-feedback/CreativePreviewFeedback.vue';
import TriggerNotification from '@/components/trigger-notification/TriggerNotification.vue';
import BoxWidget from '@/components/box-widget/BoxWidget.vue';

const debug = Debug('creative-step');

export default {
  components: {
    BoxWidget,
    comment: Comment,
    timeline: Timeline,
    CampaignNotification,
    TriggerNotification,
    'creative-preview-wrapper': CreativePreviewWrapper,
    'file-gallery': FileGallery,
    'creative-preview-feedback': CreativePreviewFeedback,
  },
  data() {
    return {
      moving: false,
      campaignFields: ['campaign.name', 'campaign.code'],
      updatingComment: false,
      loadingUpdatingComment: false,
      loadingAddingComment: false,
      commentTypes: {
        FINISH: 'FINISH', // Creative is in BRIEF status - waiting for Owner review
        REJECT: 'REJECT', // Creative is in READY status - waiting for Owner approval or rejection
        COMMENT: 'COMMENT', // Anyone can add comment
        UPDATE: 'UPDATE', // Can update initial comment
      },
      NOTIFICATION_EVENTS,
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('creative', {
      creative: 'current',
      loading: 'loading',
    }),
    ...mapState('genericConfig', ['config']),
    status() {
      return _.get(this.creative, 'status', null);
    },
    campaign() {
      const c = _.get(this.creative, 'campaign', null);
      if (c) {
        this.setCurrentCampaign(c);
      }
      return c;
    },
    notificationBox() {
      const response = { icon: 'exclamation', text: 'pos.creative.help-us-creative-perfect-ad' };
      if (this.isInHold) {
        response.icon = 'gears';
        response.text = 'pos.creative.ad-ready-for-review';
      } else if (this.isInWIP) {
        response.icon = 'gears';
        response.text = 'pos.creative.we-are-currently-working';
      } else if (this.isInReady) {
        response.text = 'pos.creative.check-the-ad-proposal';
      } else if (this.isInApproved) {
        response.icon = 'image';
        response.text = 'pos.creative.ad-is-approved';
      } else if (_.get(this, 'creative.reject_reasons', [])) {
        response.icon = 'exclamation';
        response.text = 'pos.creative.brief-was-rejected-with-reason';
      }
      return response;
    },
    reasons() {
      if (_.get(this, 'creative.reject_reasons', [])) {
        return _.get(this, 'creative.reject_reasons', []).join(', ');
      }
      return '';
    },
    isInBrief() {
      return this.status === CREATIVE_STATUSES.BRIEF;
    },
    isInOnboarding() {
      return this.status === CREATIVE_STATUSES.ONBOARDING;
    },
    isInWIP() {
      return this.status === CREATIVE_STATUSES.WIP || this.status === CREATIVE_STATUSES.HOLD;
    },
    isInReady() {
      return this.status === CREATIVE_STATUSES.READY;
    },
    isInApproved() {
      return this.status === CREATIVE_STATUSES.APPROVED;
    },
    isInHold() {
      return this.status === CREATIVE_STATUSES.HOLD;
    },
    showPreview() {
      return this.isInReady || this.isInApproved || this.isInHold;
    },
    isExternalCreative() {
      return _.get(this.creative, 'type', null) === CAMPAIGN_TYPES.EXTERNAL;
    },
    showExternalPreviews() {
      return this.isExternalCreative && this.showPreview;
    },
    currentBriefComment() {
      return _.get(this, 'creative.timeline')
        .filter(i => i.type === 'Comment')
        .map(i => i.data)
        .filter(i => i.type === 'brief')
        .find(() => true); // just return latest
    },
    comment() {
      const comment = {
        commentable_id: this.$route.params.id,
        commentable_type: COMMENTABLE_TYPES.CREATIVE,
      };

      if (this.commentTypes.UPDATE in this.commentType && this.currentBriefComment) {
        comment.text = this.currentBriefComment.text;
        comment.id = this.currentBriefComment.id;
        comment.files = this.currentBriefComment.files.map(f => ({
          ...f,
          type: f.content_type,
        }));
      }
      return comment;
    },
    canViewTimeline() {
      return permissions.canGlobal(this.user, 'Creative', ['viewTimeline']);
    },
    permissions() {
      return permissions.getPermissions(this.creative);
    },
    canUpdateBriefStatus() {
      return this.isInReady && permissions.can(this.creative, ['moveForward', 'moveBack']);
    },
    canUpdateBriefComment() {
      return permissions.can(this.creative, ['updateBriefs']);
    },
    canReject() {
      return this.isOwner;
    },
    isOwner() {
      // Currently there are equal permissions for Creative owner (Customer)
      // and Sales rep who created Campaign
      const id = _.get(this.user, 'id', null);
      return _.get(this.creative, 'owner_id', 0) === id
             || _.get(this.campaign, 'creator_id', 0) === id;
    },
    isCommentBlockVisible() {
      return !(this.isInReady && this.isOwner);
    },
    commentType() {
      if (this.isInReady && permissions.can(this.creative, ['moveBack'])) {
        return {
          REJECT: this.commentTypes.REJECT,
        };
      } else if (
        this.isInBrief && permissions.can(this.creative, ['moveForward', 'updateBriefs'])
      ) {
        return {
          UPDATE: this.commentTypes.UPDATE,
          FINISH: this.commentTypes.FINISH,
        };
      } else if (this.isInBrief && permissions.can(this.creative, ['moveForward'])) {
        return {
          FINISH: this.commentTypes.FINISH,
        };
      } else if ((this.isInBrief || this.isInOnboarding) && permissions.can(this.creative, ['updateBriefs'])) {
        return {
          UPDATE: this.commentTypes.UPDATE,
        };
      }
      return {
        COMMENT: this.commentTypes.COMMENT,
      };
    },
    landingPage() {
      return _.get(this.creative, 'campaign.landingpages.[0]', null);
    },
    firstThumbnail() {
      const leadPhoto = _.get(this.creative, 'lead_photo.thumbnail', null);
      if (leadPhoto) {
        return leadPhoto;
      }

      const firstMedia = _.get(this.creative, 'media[0].file.thumbnail', null);
      if (firstMedia) {
        return firstMedia;
      }

      return creativeDefaults.image;
    },
    facebookPage() {
      return _.get(this.campaign, 'fbpage', {});
    },
    getCreativeExternalPreviews() {
      return _.get(this.creative, 'external_previews', []).map(p => ({
        ...p,
        href: p.path,
        type: p.content_type,
      }));
    },
    timelineItems() {
      let items = _.get(this, 'creative.timeline', []);
      const agentBrief = _.get(this, 'campaign.comments', [])
        .filter(i => i.type === 'brief')[0] || null;

      if (agentBrief) {
        items = [...items, { type: 'Comment', data: agentBrief }];
      }

      return items;
    },
  },
  methods: {
    ...mapActions('campaign', ['setCurrentCampaign']),
    ...mapActions('creative', ['getCreative', 'moveForward', 'moveBack', 'reloadCreative']),
    ...mapActions('genericConfig', { updateGenericConfig: 'updateConfig' }),
    processComment(update = false) {
      if (this.commentTypes.UPDATE in this.commentType && update) {
        return this.update();
      } else if (this.commentTypes.FINISH in this.commentType) {
        return this.finish(true);
      } else if (this.commentTypes.REJECT in this.commentType) {
        return this.reject();
      }

      return Promise.resolve(this.updateComment(update))
        .catch(e => debug('error in processComment()', e));
    },
    updateComment(update = false) {
      let msg = this.$t('pos.comment.comment-added');

      if (this.commentTypes.UPDATE in this.commentType) {
        msg = this.$t('pos.comment.brief-updated');
      }

      if (this.commentTypes.UPDATE in this.commentType && update) {
        this.loadingUpdatingComment = true;
      } else {
        this.loadingAddingComment = true;
      }

      this.updatingComment = true;

      return this.$refs.comment.updateComment()
        .then(() => {
          this.$toast.success({ message: msg });
        })
        .then(() => this.reloadCreative())
        .then(() => {
          this.updatingComment = false;
          this.loadingUpdatingComment = false;
          this.loadingAddingComment = false;
        })
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.updatingComment = false;
          this.loadingUpdatingComment = false;
          this.loadingAddingComment = false;
          throw e;
        });
    },
    finish(updateComment = false) {
      return Promise.resolve(updateComment ? this.updateComment() : null)
        .then(() => this.moveForward(this.creative.id))
        .then(() => this.$toast.info({ message: this.$t('pos.creative.creative-next') }))
        .then(() => this.reloadCreative())
        .catch(e => debug('error in finish()', e));
    },
    update() {
      // #32911 Add whole page refresh when updating comment to update file list
      return Promise.resolve(this.updateComment(true))
        .then(() => this.reloadPage())
        .catch(e => debug('error in update()', e));
    },
    reloadPage() {
      window.location.reload(true);
    },
    reject() {
      return this.moveBack({
        id: this.creative.id,
        data: this.$refs.comment.getCommentData(),
      })
        .then(() => {
          if (this.$refs.comment) {
            this.$refs.comment.clearCommentData();
          }
          this.$toast.info({ message: this.$t('pos.creative.creative-rejected') });
          return this.reloadCreative();
        })
        .catch(e => {
          this.$toast.error({ message: e.toString() });
        });
    },
    onMoveForward() {
      this.moving = true;
      const fin = () => this.moving = false;
      this.moveForward(this.creative.id)
        .then(() => {
          this.$toast.success({ message: this.$t('pos.campaign.creatives.adv-builder.complete') });
          fin();
        })
        .catch(error => {
          this.$toast.error({ message: adapi.translateAdapiErrors(error.errors, this.$t) });
          fin();
        });
    },
  },
  mounted() {
    this.updateGenericConfig();
  },
};



// WEBPACK FOOTER //
// ./src/components/rabbit/creative-step/CreativeStep.js